import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

const labelOf = key => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
const isAnswer = key => !key.startsWith('_') && !['submit', 'receiving_customer_id', 'notes'].includes(key);

export const referralAnswers = data => Object.fromEntries(Object.entries(data || {}).filter(([key]) => isAnswer(key)));
export const filledAnswer = ([, value]) => value !== '' && value !== null && value !== undefined;

// Legacy referrals may have no available template. Never expose their structured
// answers as JSON or HTML; retain nested fields, repeated rows and checkbox values.
function AnswerValue({ value }) {
  if (value === null || value === undefined || value === '') return <Typography variant="body2">Not provided</Typography>;
  if (typeof value === 'boolean') return <Typography variant="body2">{value ? 'Yes' : 'No'}</Typography>;
  if (Array.isArray(value)) {
    if (!value.length) return <Typography variant="body2">None selected</Typography>;
    return <Stack spacing={1}>{value.map((item, index) => <Box key={index}><AnswerValue value={item} /></Box>)}</Stack>;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([key]) => isAnswer(key));
    // Form.io selectboxes store an object mapping option values to booleans.
    if (entries.length && entries.every(([, selected]) => typeof selected === 'boolean')) {
      const selected = entries.filter(([, checked]) => checked).map(([key]) => labelOf(key));
      return <Typography variant="body2">{selected.join(', ') || 'None selected'}</Typography>;
    }
    // Uploaded files and resource selections often provide a human-readable name.
    const name = value.originalName || value.label || value.name;
    const namedValue = entries.every(([key]) => ['originalName', 'label', 'name', 'value', 'id', '_id', 'url', 'storage', 'size', 'type', 'data'].includes(key));
    if (namedValue && typeof name === 'string') return <Typography variant="body2">{name}</Typography>;
    return entries.length ? <ReferralAnswers entries={entries} /> : <Typography variant="body2">Not provided</Typography>;
  }
  return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{String(value)}</Typography>;
}

export default function ReferralAnswers({ entries }) {
  return (
    <Stack spacing={1.5}>
      {entries.filter(([key]) => isAnswer(key)).map(([key, value]) => (
        <Box key={key}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{labelOf(key)}</Typography>
          <AnswerValue value={value} />
        </Box>
      ))}
    </Stack>
  );
}